// Catalog.ID Client — Publishing the receive address
// ============================================================================
// The optional public claim of registration-wallets §4.11 / D4: "Publish my
// receive address so others can pay me", DEFAULT OFF.
//
// It is an ordinary self-asserted claim on the ordinary publication path — the
// value is encrypted to the account's attribute key, the claim is created, and
// publication makes it readable. Two things make it worth its own module:
//
//  1. **The checksum is checked before anything is created.** A published
//     receive address is a payment target other people copy. A mistyped one
//     sends money nowhere and nobody finds out — least of all the member, who
//     simply never gets paid. The 34-character form carries a type-aware
//     checksum, so this is cheap and total.
//
//  2. **A spending key is refused outright.** `CASH-…` and `FUND-…` differ by
//     one ordinal in one checksum character, and publishing the first would
//     hand the wallet to everybody who reads the profile. `verifyReceiveAddress`
//     accepts FUND and nothing else, which is why it is used here rather than
//     `verifyWalletKey`.
//
// DOM-free and injected, like the claim steps next door.

import { displayWalletKey, verifyReceiveAddress } from '../crypto/bitcash';
import { encryptClaim, reEncryptClaimKey } from '../crypto/claims';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import {
    publishReceiveAddressClaim, RECEIVE_ADDRESS_CLAIM_TYPE,
} from '../pane-types/bitcash-wallets-core';

/** The claim type the node registered for this (§4.11). Re-exported from the
 *  Settings core so the print step and the pane can never name it differently. */
export const BITCASH_RECEIVE_ADDRESS_CLAIM = RECEIVE_ADDRESS_CLAIM_TYPE;

export interface PublishAddressDeps {
    encryptClaim: typeof encryptClaim;
    createClaim: typeof claimsApi.createClaim;
    publishClaim: typeof publicClaimsApi.publishClaim;
    /** The operators whose review the publication needs. */
    listOperators: typeof publicClaimsApi.listOperators;
    /** Re-wrap the claim key for one operator (ECDH, sharer → recipient). */
    reEncryptClaimKey: typeof reEncryptClaimKey;
}

export const defaultPublishAddressDeps: PublishAddressDeps = {
    encryptClaim,
    createClaim: claimsApi.createClaim,
    publishClaim: publicClaimsApi.publishClaim,
    listOperators: publicClaimsApi.listOperators,
    reEncryptClaimKey,
};

export interface PublishAddressResult {
    status: 'published' | 'refused' | 'unavailable';
    /** Set on success. NOT set when the publish half failed after the claim was
     *  created: the shared helper throws without surfacing the uuid, so a retry
     *  creates a second claim rather than republishing the first. Harmless (an
     *  unpublished claim is private and the member can delete it) but worth
     *  knowing — Settings' own publish action has the same shape. */
    claimUuid?: string;
    message?: string;
}

/**
 * Publish a wallet's receive address as a public claim. Never throws: this is a
 * checkbox at the print step, and a failed publication must not cost a member
 * their kit. The claim can be published later from Settings either way.
 */
export async function publishReceiveAddress(
    args: { receiveAddr: string; attrPublicKey: Uint8Array; attrPrivateKey: Uint8Array },
    deps: PublishAddressDeps = defaultPublishAddressDeps,
): Promise<PublishAddressResult> {
    // The checksum, the operator lookup, the claim, the per-operator re-wrap and
    // the publish all live in ONE implementation (`bitcash-wallets-core`), which
    // Settings uses too. That is not tidiness: publication needs the claim key
    // re-wrapped for every operator, and `PublicClaimController.Publish` refuses
    // a request without `operatorKeys` — so a second, simpler implementation
    // here would be a checkbox that silently publishes nothing.
    // §4.11 pins the published VALUE as the 34-character display form — it is
    // what a payer reads off a profile and types into a wallet. The shared
    // helper publishes whatever form it is handed, so the normalisation happens
    // here, before it: a compact address published as-is would be correct but
    // unreadable, and two accounts would publish the same address in two shapes.
    const value = verifyReceiveAddress(args.receiveAddr)
        ? displayWalletKey(args.receiveAddr)! : args.receiveAddr;

    try {
        const result = await publishReceiveAddressClaim({
            encryptClaim: deps.encryptClaim,
            createClaim: deps.createClaim,
            listOperators: deps.listOperators,
            reEncryptClaimKey: deps.reEncryptClaimKey,
            publishClaim: deps.publishClaim,
            ownerPublicKey: args.attrPublicKey,
            ownerSecretKey: args.attrPrivateKey,
        }, { address: value });
        return { status: 'published', claimUuid: result.claimUuid };
    } catch (err) {
        // A malformed address and a node with no operator are both REFUSALS —
        // the member's own input or their node's posture, not a blip — and the
        // helper's own sentence is the one to show. Everything else is a blip.
        const message = err instanceof Error ? err.message : '';
        if (/receive address|operator/i.test(message)) {
            return { status: 'refused', message };
        }
        return {
            status: 'unavailable',
            message: 'Your receive address could not be published just now. Everything else is '
                + 'complete — you can publish it later from Settings.',
        };
    }
}

/**
 * The one sentence a member who ticked "Publish my receive address" reads beside
 * their kit, whatever happened. `null` means there was no address to publish —
 * the registration set up no wallet. A refusal is passed on word for word: on a
 * node with no operator yet it is "No operator is available to review the
 * publication…", which is exactly what the member needs to know (staging test
 * 2026-10-15: that sentence used to be dropped, and the box looked as if it had
 * worked).
 */
export function publishOutcomeNotice(result: PublishAddressResult | null): string {
    if (result === null) {
        return 'Your receive address was not published: this registration has no wallet to publish one '
            + 'for. You can publish an address later in Settings.';
    }
    switch (result.status) {
        case 'published':
            return 'Your receive address was submitted for your public profile. It appears there once an '
                + 'operator has reviewed it.';
        case 'refused':
            return `Your receive address was not published: ${result.message ?? 'the server refused it.'} `
                + 'You can publish it later in Settings.';
        case 'unavailable':
            return result.message ?? 'Your receive address could not be published just now. You can publish '
                + 'it later in Settings.';
    }
}
